import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Package, ShoppingBag, Users, Mail, AlertCircle } from "lucide-react";
import { api, formatINR } from "../../lib/api";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard", { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ kpis: {}, recent_orders: [], top_products: [] }));
  }, []);

  if (!data) return <div className="p-10 text-brand-muted">Loading dashboard…</div>;
  const k = data.kpis || {};

  const cards = [
    { l: "Total Revenue", v: formatINR(k.total_revenue || 0), sub: `${formatINR(k.week_revenue || 0)} this week`, icon: TrendingUp, tone: "maroon" },
    { l: "Orders", v: k.total_orders || 0, sub: `${k.orders_today || 0} today · ${k.orders_week || 0} this week`, icon: ShoppingBag, tone: "gold" },
    { l: "Products", v: k.total_products || 0, sub: `${k.low_stock || 0} low stock`, icon: Package, tone: "maroon" },
    { l: "Customers", v: k.total_customers || 0, sub: `${k.newsletter_subs || 0} newsletter subs`, icon: Users, tone: "gold" },
    { l: "Abandoned Carts", v: k.abandoned_carts || 0, sub: "Ready for recovery", icon: AlertCircle, tone: "maroon" },
  ];

  return (
    <div className="p-8 lg:p-12" data-testid="admin-dashboard">
      <p className="label-tiny text-brand-gold">Overview</p>
      <h1 className="font-serif-brand text-4xl text-brand-charcoal mt-2">Dashboard</h1>
      <p className="text-brand-muted mt-2">Welcome back. Here's what's happening at the atelier.</p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
        {cards.map((c) => (
          <div key={c.l} className={`p-5 border ${c.tone === "maroon" ? "border-brand-maroon/30 bg-brand-ivory" : "border-brand-gold/40 bg-brand-ivory"}`} data-testid={`kpi-${c.l.toLowerCase().replace(/\s+/g, '-')}`}>
            <c.icon size={20} strokeWidth={1.4} className={c.tone === "maroon" ? "text-brand-maroon" : "text-brand-gold"} />
            <p className="label-tiny text-brand-muted mt-3">{c.l}</p>
            <p className="font-serif-brand text-2xl text-brand-charcoal mt-1">{c.v}</p>
            <p className="text-[11px] text-brand-muted mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        <div className="bg-brand-ivory border border-brand-gold/25 p-6">
          <div className="flex items-center justify-between mb-4">
            <div><p className="label-tiny text-brand-gold">Recent</p><h2 className="font-serif-brand text-2xl text-brand-charcoal mt-1">Latest Orders</h2></div>
            <Link to="/admin/orders" className="text-xs tracking-[0.2em] uppercase text-brand-maroon hover:underline">View all</Link>
          </div>
          {data.recent_orders.length === 0 ? <p className="text-sm text-brand-muted">No orders yet.</p> : (
            <div className="space-y-3">
              {data.recent_orders.map((o) => (
                <div key={o.order_number} className="flex items-center justify-between py-2 border-b border-brand-gold/15 text-sm">
                  <div>
                    <p className="font-medium text-brand-charcoal">{o.order_number}</p>
                    <p className="text-xs text-brand-muted">{o.shipping.full_name} · {o.status}</p>
                  </div>
                  <p className="text-brand-maroon font-semibold">{formatINR(o.total)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-brand-ivory border border-brand-gold/25 p-6">
          <p className="label-tiny text-brand-gold">Top Movers</p>
          <h2 className="font-serif-brand text-2xl text-brand-charcoal mt-1 mb-4">Best-Selling Products</h2>
          {data.top_products.length === 0 ? <p className="text-sm text-brand-muted">No sales yet.</p> : (
            <div className="space-y-3">
              {data.top_products.map((p) => (
                <div key={p._id} className="flex items-center justify-between py-2 border-b border-brand-gold/15 text-sm">
                  <div>
                    <p className="font-medium text-brand-charcoal">{p.name}</p>
                    <p className="text-xs text-brand-muted">Sold: {p.sold}</p>
                  </div>
                  <p className="text-brand-maroon font-semibold">{formatINR(p.revenue)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
